import axios from "axios";
import React, {
  useState,
  useEffect,
} from "react";
import {
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  useDispatch,
  useSelector,
} from "react-redux";
import {
  createOrder,
} from "../../actions/orderActions";
import {
  useCart,
} from "../../components/CartContext";
import {
  useRestaurantSession,
} from "../../components/RestaurantSessionProvider";
import {
  isSessionExpiredError,
  getSessionExpiredMessage,
} from "../../utils/sessionErrorHandler";
import { fetchPriceSummary } from "../../actions/priceActions";

import "./css/OrderScreen.css";
function OrderScreen() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    restaurantId,
    tableId,
  } = useParams();
  const [paymentOptions, setPaymentOptions] = useState(null);
  const onlineAllowed = paymentOptions?.allow_online_payment === true;
  const cashAllowed = paymentOptions?.allow_cash_payment === true;
  useEffect(() => {
    if (!restaurantId) return;
    let active = true;
    setPaymentOptions(null);
    axios.get(`/api/restaurants/${encodeURIComponent(restaurantId)}/payment-options/`)
      .then(({ data }) => { if (active) setPaymentOptions(data); })
      .catch(() => { if (active) setPaymentOptions({ allow_cash_payment: false, allow_online_payment: false }); });
    return () => { active = false; };
  }, [restaurantId]);
  // =====================================================
  // DINING SESSION
  // =====================================================
  const {
    sessionToken,
  } = useRestaurantSession();
  // =====================================================
  // CART
  // =====================================================
  const {
    cart,
    clearCart,
  } = useCart();
  // =====================================================
  // PRICE SUMMARY FROM CHECKOUT
  // =====================================================
  const { loading: priceLoading, summary: priceSummary, error: priceError } =
    useSelector((state) => state.price || {});

  useEffect(() => {
    if (!cart?.length) return;
    dispatch(fetchPriceSummary(restaurantId, cart.map((item) => ({
      menu_item_id: item.id,
      quantity: item.quantity,
    }))));
  }, [dispatch, restaurantId, cart]);

  const priceReady = !priceLoading && !priceError && Boolean(priceSummary) &&
    priceSummary.total != null && Number.isFinite(Number(priceSummary.total));
  const subtotal = priceSummary?.subtotal ?? 0;
  const tax = priceSummary?.tax ?? 0;
  const serviceCharge = priceSummary?.service_charge ?? 0;
  const total = priceSummary?.total ?? 0;

  // =====================================================
  // STATE
  // =====================================================
  const [
    paymentMethod,
    setPaymentMethod,
  ] = useState("");
  const [
    notes,
    setNotes,
  ] = useState("");
  const [
    loading,
    setLoading,
  ] = useState(false);
  const [
    error,
    setError,
  ] = useState("");
  useEffect(() => {
    if (!paymentOptions) return;
    setPaymentMethod((current) => {
      if (current === "ONLINE" && onlineAllowed) return current;
      if (current === "CASH" && cashAllowed) return current;
      return cashAllowed ? "CASH" : onlineAllowed ? "ONLINE" : "";
    });
  }, [paymentOptions, cashAllowed, onlineAllowed]);
  // =====================================================
  // HOME PATH
  // =====================================================
  const homePath =
    `/${restaurantId}/table/${tableId}`;
  // =====================================================
  // FORMAT PRICE
  // =====================================================
  const formatPrice = (value) => {
    return Number(
      value || 0
    ).toFixed(2);
  };
  // =====================================================
  // BACK TO CHECKOUT
  // =====================================================
  const handleBack = () => {
    navigate(
      `${homePath}/checkout`
    );
  };
  // =====================================================
  // PLACE ORDER
  // =====================================================
  const handlePlaceOrder =
    async () => {
      // -------------------------------------------------
      // PAYMENT METHOD
      // -------------------------------------------------
      if (!paymentOptions || !paymentMethod || (paymentMethod === "ONLINE" && !onlineAllowed) || (paymentMethod === "CASH" && !cashAllowed)) {
        setError(
          "Please select a payment method."
        );
        return;
      }
      // -------------------------------------------------
      // CART
      // -------------------------------------------------
      if (
        !cart ||
        cart.length === 0
      ) {
        setError(
          "Your cart is empty."
        );
        return;
      }
      // -------------------------------------------------
      // DINING SESSION
      // -------------------------------------------------
      if (!priceReady) {
      setError("Final price is not ready. Please wait and try again.");
      return;
    }

    if (!sessionToken) {
        setError(
          "Dining session is not ready. Please try again."
        );
        return;
      }
      try {
        setLoading(true);
        setError("");
        // =================================================
        // COMMON ORDER PAYLOAD
        // =================================================
        const orderData = {
          items:
            cart.map(
              (item) => ({
                menu_item_id:
                  item.id,
                quantity:
                  item.quantity,
              })
            ),
          // -------------------------------------------------
          // DINING SESSION
          // -------------------------------------------------
          session_token:
            sessionToken,
          // -------------------------------------------------
          // PAYMENT
          // -------------------------------------------------
          payment_method:
            paymentMethod,
          // -------------------------------------------------
          // TABLE
          // -------------------------------------------------
          table_id:
            Number(tableId),
          // -------------------------------------------------
          // NOTES
          // -------------------------------------------------
          notes:
            notes.trim(),
        };
        // =================================================
        // CREATE ORDER
        // =================================================
        const response =
          await dispatch(
            createOrder(
              restaurantId,
              orderData
            )
          );
        console.log(
          "CREATE ORDER RESPONSE:",
          response
        );
        const orderId =
          response?.order_id ||
          response?.id;
        const confirmedTotal = Number(response?.total ?? priceSummary?.total);
      if (!Number.isFinite(confirmedTotal)) {
        throw new Error("A valid order total was not returned by the server.");
      }

      if (!orderId) {
          throw new Error(
            "Order was created but no order ID was returned."
          );
        }
        // =================================================
        // CASH PAYMENT
        // =================================================
        if (
          paymentMethod === "CASH"
        ) {
          clearCart();
          navigate(
            `${homePath}/success`,
            {
              state: {
                orderId,
                paymentMethod:
                  "CASH",
                total:
                  confirmedTotal,
              },
            }
          );
          return;
        }
        // =================================================
        // ONLINE PAYMENT
        // =================================================
        if (
          paymentMethod === "ONLINE"
        ) {
          navigate(
            `${homePath}/payment`,
            {
              state: {
                orderId,
                subtotal:
                  Number(subtotal),
                tax:
                  Number(tax),
                serviceCharge:
                  Number(serviceCharge),
                total:
                  confirmedTotal,
              },
            }
          );
        }
      } catch (err) {
        console.error(
          "Create order error:",
          err
        );
        // =================================================
        // DINING SESSION EXPIRED
        // =================================================
        if (
          isSessionExpiredError(err)
        ) {
          setError(
            "Your table session has ended. " +
            "Please scan the table QR code again " +
            "to start a new session."
          );
          return;
        }
        // =================================================
        // OTHER ERRORS
        // =================================================
        setError(
          getSessionExpiredMessage(err)
        );
      } finally {
        setLoading(false);
      }
    };
  // =====================================================
  // RENDER
  // =====================================================
  return (
    <main className="theme8-order-page">
      {/* =================================================
          HEADER
      ================================================= */}
      <section className="theme8-order-header">
        <div className="theme8-order-container">
          <button
            type="button"
            className="theme8-order-back"
            onClick={handleBack}
          >
            <span>
              ←
            </span>
            Back to Checkout
          </button>
          <div className="theme8-order-title-row">
            <div>
              <span className="theme8-order-label">
                ONE LAST STEP
              </span>
              <h1>
                Place Your Order
              </h1>
            </div>
            <div className="theme8-order-table">
              <span>
                TABLE
              </span>
              <strong>
                {tableId}
              </strong>
            </div>
          </div>
        </div>
      </section>
      {/* =================================================
          CONTENT
      ================================================= */}
      <section className="theme8-order-content">
        <div className="theme8-order-container">
          <div className="theme8-order-layout">
            {/* =================================================
                LEFT SIDE
            ================================================= */}
            <div className="theme8-order-main">
              {/* =================================================
                  PAYMENT METHOD
              ================================================= */}
              <section className="theme8-order-section">
                <div className="theme8-order-section-title">
                  <span>
                    01
                  </span>
                  <h2>
                    Choose payment
                  </h2>
                </div>
                <div className="theme8-payment-options">
                  {/* =================================================
                      CASH
                  ================================================= */}
                  <button
                    type="button"
                    disabled={!cashAllowed}
                    style={!cashAllowed ? { opacity: 0.45, cursor: "not-allowed" } : undefined}
                    className={
                      `theme8-payment-option ${
                        paymentMethod === "CASH"
                          ? "selected"
                          : ""
                      }`
                    }
                    onClick={() =>
                      cashAllowed && setPaymentMethod(
                        "CASH"
                      )
                    }
                  >
                    <div className="theme8-payment-icon">
                      ₹
                    </div>
                    <div className="theme8-payment-info">
                      <strong>
                        CASH
                      </strong>
                      <span>
                        Pay by cash after
                        your order is served.
                      </span>
                    </div>
                    <div className="theme8-payment-check">
                      {paymentMethod === "CASH"
                        ? "✓"
                        : ""}
                    </div>
                  </button>
                  {/* =================================================
                      ONLINE
                  ================================================= */}
                  <button
                    type="button"
                    disabled={!onlineAllowed}
                    style={!onlineAllowed ? { opacity: 0.45, cursor: "not-allowed" } : undefined}
                    className={
                      `theme8-payment-option ${
                        paymentMethod === "ONLINE"
                          ? "selected"
                          : ""
                      }`
                    }
                    onClick={() =>
                      onlineAllowed && setPaymentMethod(
                        "ONLINE"
                      )
                    }
                  >
                    <div className="theme8-payment-icon">
                      ₹
                    </div>
                    <div className="theme8-payment-info">
                      <strong>
                        Online Payment
                      </strong>
                      <span>
                        {onlineAllowed ? "Secure payment powered by Razorpay." : "Online payment unavailable."}
                      </span>
                    </div>
                    <div className="theme8-payment-check">
                      {paymentMethod === "ONLINE"
                        ? "✓"
                        : ""}
                    </div>
                  </button>
                </div>
              </section>
              {/* =================================================
                  NOTES
              ================================================= */}
              <section className="theme8-order-section">
                <div className="theme8-order-section-title">
                  <span>
                    02
                  </span>
                  <h2>
                    Special instructions
                  </h2>
                </div>
                <div className="theme8-notes-box">
                  <textarea
                    value={notes}
                    onChange={(event) =>
                      setNotes(
                        event.target.value
                      )
                    }
                    placeholder={
                      "Any special request for the kitchen? " +
                      "For example: less spicy, no onions..."
                    }
                    rows={5}
                  />
                </div>
              </section>
              {/* =================================================
                  TABLE
              ================================================= */}
              <div className="theme8-order-table-card">
                <div className="theme8-order-table-number">
                  {tableId}
                </div>
                <div>
                  <span>
                    YOUR TABLE
                  </span>
                  <h3>
                    Table {tableId}
                  </h3>
                  <p>
                    Your order will be prepared
                    and served at this table.
                  </p>
                </div>
              </div>
              {/* =================================================
                  ERROR
              ================================================= */}
              {(error || priceError) && (
                <div className="theme8-order-error">
                  <strong>
                    Unable to continue
                  </strong>
                  <p>
                    {error || (typeof priceError === "string" ? priceError : "Unable to calculate the final price.")}
                  </p>
                </div>
              )}
            </div>
            {/* =================================================
                ORDER SUMMARY
            ================================================= */}
            <aside className="theme8-order-summary">
              <span className="theme8-order-summary-label">
                03 · ORDER SUMMARY
              </span>
              <h2>
                Your Order
              </h2>
              {/* =================================================
                  CART ITEMS
              ================================================= */}
              <div className="theme8-order-summary-items">
                {cart.map(
                  (item) => (
                    <div
                      className="theme8-order-summary-item"
                      key={item.id}
                    >
                      <div>
                        <span>
                          {item.quantity} ×{" "}
                          {item.name}
                        </span>
                      </div>
                      <strong>
                        ₹
                        {formatPrice(
                          Number(item.price) *
                          Number(item.quantity)
                        )}
                      </strong>
                    </div>
                  )
                )}
              </div>
              <div className="theme8-order-divider">
              </div>
              {/* =================================================
                  SUBTOTAL
              ================================================= */}
              <div className="theme8-order-price-row">
                <span>
                  Subtotal
                </span>
                <strong>
                  ₹
                  {formatPrice(
                    subtotal
                  )}
                </strong>
              </div>
              {/* =================================================
                  TAX
              ================================================= */}
              <div className="theme8-order-price-row">
                <span>
                  Tax
                </span>
                <strong>
                  ₹
                  {formatPrice(
                    tax
                  )}
                </strong>
              </div>
              {/* =================================================
                  SERVICE CHARGE
              ================================================= */}
              <div className="theme8-order-price-row">
                <span>
                  Service Charge
                </span>
                <strong>
                  ₹
                  {formatPrice(
                    serviceCharge
                  )}
                </strong>
              </div>
              <div className="theme8-order-divider">
              </div>
              {/* =================================================
                  TOTAL
              ================================================= */}
              <div className="theme8-order-total">
                <span>
                  TOTAL
                </span>
                <strong>
                  ₹
                  {formatPrice(
                    total
                  )}
                </strong>
              </div>
              {/* =================================================
                  PLACE ORDER
              ================================================= */}
              <button
                type="button"
                className="theme8-place-order-button"
                onClick={
                  handlePlaceOrder
                }
                disabled={
                  loading ||
                  !paymentMethod ||
                !sessionToken ||
                !priceReady ||
                !cart?.length
                }
              >
                {loading
                  ? "Creating Order..."
                  : "Place Order"}
                {!loading && (
                  <span>
                    →
                  </span>
                )}
              </button>
              {/* =================================================
                  SECURE NOTE
              ================================================= */}
              <p className="theme8-order-secure-note">
                🔒 Your order information
                is securely processed.
              </p>
            </aside>
          </div>
        </div>
      </section>
    </main>
  );
}
export default OrderScreen;